import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errMsg, fmtDate } from "@/lib/api";
import { btnPrimary, Skeleton } from "@/components/portal/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function ParticipantsDialog({ event, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!event) return;
    setData(null);
    api.get(`/admin/events/${event.id}/participants`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [event]);

  const exportXlsx = async () => {
    setBusy(true);
    try {
      await downloadFile(`/admin/events/${event.id}/participants.xlsx`, `peserta-${event.title.replace(/[^\w]+/g, "-").toLowerCase()}.xlsx`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="participants-dialog">
        <DialogHeader><DialogTitle>Peserta · {event?.title}</DialogTitle></DialogHeader>
        {!data ? <Skeleton className="h-40" /> : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap gap-2" data-testid="attendance-summary">
                <span className="text-xs font-bold bg-slate-100 text-slate-700 rounded-full px-3 py-1.5" data-testid="participants-count">{data.summary.registered} terdaftar{event.quota ? ` / kuota ${event.quota}` : ""}</span>
                <span className="text-xs font-bold bg-emerald-100 text-emerald-700 rounded-full px-3 py-1.5" data-testid="attendance-scanned">{data.summary.scanned} hadir (discan)</span>
                <span className="text-xs font-bold bg-amber-100 text-amber-700 rounded-full px-3 py-1.5" data-testid="attendance-pending">{data.summary.not_scanned} belum hadir</span>
                {Object.entries(data.summary.by_ticket_type).map(([t, v]) => (
                  <span key={t} className="text-xs font-bold bg-blue-50 text-blue-700 rounded-full px-3 py-1.5" data-testid={`attendance-type-${t}`}>{t}: {v.scanned}/{v.total}</span>
                ))}
              </div>
              <button onClick={exportXlsx} disabled={busy || !data.participants.length} className={`${btnPrimary} !py-2.5`} data-testid="participants-export-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Ekspor Excel</button>
            </div>
            <div className="overflow-x-auto border border-slate-100 rounded-2xl">
              <table className="w-full text-sm" data-testid="participants-table">
                <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50"><tr>{["No", "Nama", "NIG", "WhatsApp", "Tiket", "Kode", "Daftar", "Scan Masuk"].map((h) => <th key={h} className="px-4 py-3 font-bold">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.participants.map((p, i) => (
                    <tr key={p.id} data-testid={`participant-row-${p.id}`}>
                      <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                      <td className="px-4 py-3 font-bold text-slate-900">{p.name}</td>
                      <td className="px-4 py-3 tabular-nums font-semibold">{p.nig}</td>
                      <td className="px-4 py-3 text-slate-600">{p.phone || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{p.ticket_type || "Reguler"}</td>
                      <td className="px-4 py-3 text-xs font-bold tracking-wide text-slate-500">{p.ticket_code || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(p.created_at, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="px-4 py-3 text-xs">
                        {p.scanned_at
                          ? <span className="font-bold text-emerald-600">{fmtDate(p.scanned_at, { hour: "2-digit", minute: "2-digit" })}{p.scanned_by ? ` · ${p.scanned_by}` : ""}</span>
                          : <span className="text-slate-400">Belum hadir</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.participants.length === 0 && <p className="p-6 text-center text-slate-500">Belum ada peserta.</p>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
